import React, { useState, useEffect } from 'react';
import styles from './NotePriorities.module.css';

const NotePriorityForm = ({ priority, onSubmit, onCancel, loading, checkNameExists, checkWeightExists }) => {
    const [formData, setFormData] = useState({
        priorityName: '',
        priorityWeight: 0
    });

    const [errors, setErrors] = useState({});
    const [warnings, setWarnings] = useState({});

    // Initialize form with existing data when editing
    useEffect(() => {
        if (priority) {
            setFormData({
                priorityName: priority.priorityName || '',
                priorityWeight: priority.priorityWeight || 0
            });
        }
    }, [priority]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        const newValue = name === 'priorityWeight' ? (parseInt(value) || 0) : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }

        // Clear warning when user changes the value
        if (warnings[name]) {
            setWarnings(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    // Handle blur events for async validation
    const handleBlur = async (e) => {
        const { name, value } = e.target;

        if (name === 'priorityName' && value.trim()) {
            // Check if name exists (only when creating or name changed)
            if (!priority || priority.priorityName !== value) {
                const exists = await checkNameExists(value);
                if (exists) {
                    setErrors(prev => ({
                        ...prev,
                        priorityName: 'A priority with this name already exists'
                    }));
                }
            }
        }

        if (name === 'priorityWeight') {
            const weight = parseInt(value) || 0;
            // Check if weight exists (only when creating or weight changed)
            if (!priority || priority.priorityWeight !== weight) {
                const exists = await checkWeightExists(weight);
                if (exists) {
                    setWarnings(prev => ({
                        ...prev,
                        priorityWeight: 'Another priority already uses this weight'
                    }));
                }
            }
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.priorityName.trim()) {
            newErrors.priorityName = 'Priority name is required';
        } else if (formData.priorityName.length > 200) {
            newErrors.priorityName = 'Priority name must be 200 characters or less';
        }

        if (formData.priorityWeight === null || formData.priorityWeight === undefined) {
            newErrors.priorityWeight = 'Priority weight is required';
        } else if (formData.priorityWeight < 0) {
            newErrors.priorityWeight = 'Priority weight must be 0 or greater';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formHeader}>
                <h3>{priority ? 'Edit Note Priority' : 'Add New Note Priority'}</h3>
            </div>

            <div className={styles.formBody}>
                {/* Priority Name */}
                <div className={styles.field}>
                    <label htmlFor="priorityName" className={styles.label}>
                        Priority Name *
                    </label>
                    <input
                        id="priorityName"
                        name="priorityName"
                        type="text"
                        value={formData.priorityName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${styles.input} ${errors.priorityName ? styles.inputError : ''}`}
                        placeholder="e.g., Critical, High, Medium, Low"
                        maxLength={200}
                        disabled={loading}
                    />
                    {errors.priorityName && (
                        <span className={styles.errorText}>{errors.priorityName}</span>
                    )}
                    <small className={styles.hint}>
                        {formData.priorityName.length}/200 characters
                    </small>
                </div>

                {/* Priority Weight */}
                <div className={styles.field}>
                    <label htmlFor="priorityWeight" className={styles.label}>
                        Priority Weight *
                    </label>
                    <input
                        id="priorityWeight"
                        name="priorityWeight"
                        type="number"
                        value={formData.priorityWeight}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${styles.input} ${errors.priorityWeight ? styles.inputError : ''}`}
                        placeholder="Higher number = higher priority"
                        min={0}
                        disabled={loading}
                    />
                    {errors.priorityWeight && (
                        <span className={styles.errorText}>{errors.priorityWeight}</span>
                    )}
                    {warnings.priorityWeight && (
                        <span className={styles.warningText}>{warnings.priorityWeight}</span>
                    )}
                    <small className={styles.hint}>
                        Higher weight values indicate higher priority (e.g., Critical: 100, High: 75, Medium: 50, Low: 25)
                    </small>
                </div>
            </div>

            <div className={styles.formFooter}>
                <button
                    type="button"
                    onClick={onCancel}
                    className={styles.cancelButton}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (priority ? 'Update' : 'Create')}
                </button>
            </div>
        </form>
    );
};

export default NotePriorityForm;
